import { action, observable } from 'mobx';
import { ShippingCountryState } from '../../../../utils/shipping';
import { CountryLocationData } from './storeLocatorTypes';

const LOCAL_STORAGE_KEY_LATITUDE = 'alo-users-latitude';
const LOCAL_STORAGE_KEY_LONGITUDE = 'alo-users-longitude';
const LOCAL_STORAGE_KEY_ZIPCODE = 'alo-users-zipcode';
const LOCAL_STORAGE_KEY_SDD_ZIPCODE = 'alo-users-sdd-zipcode';
const LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA = 'alo-users-location-data';

const selectedSddZipcodeFromLS = localStorage.getItem(LOCAL_STORAGE_KEY_SDD_ZIPCODE);

const getCountryLocationDataFromLS = (): CountryLocationData | null => {
  //new local storage for zip lat and long based on country
  const value = localStorage.getItem(LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA);
  return value ? JSON.parse(value) : null;
};

const currentCountryCode = ShippingCountryState.countryCode;
const userSelectedCountryLocations = getCountryLocationDataFromLS();
const currentCountryLocationData = userSelectedCountryLocations?.[currentCountryCode] ?? null;

let selectedZipcodeFromLS: string | null;
let selectedLatitudeFromLS: string | null;
let selectedLongitudeFromLS: string | null;
//Read from new local storage first, if not available then fallback to old local storage keys for backward compatibility
if (currentCountryLocationData) {
  selectedZipcodeFromLS = currentCountryLocationData?.zip ?? null;
  selectedLatitudeFromLS = currentCountryLocationData?.lat ?? null;
  selectedLongitudeFromLS = currentCountryLocationData?.long ?? null;
} else {
  selectedZipcodeFromLS = localStorage.getItem(LOCAL_STORAGE_KEY_ZIPCODE) ?? null;
  selectedLatitudeFromLS = localStorage.getItem(LOCAL_STORAGE_KEY_LATITUDE) ?? null;
  selectedLongitudeFromLS = localStorage.getItem(LOCAL_STORAGE_KEY_LONGITUDE) ?? null;
}

export const customerLocationState = observable({
  coordinates:
    selectedLatitudeFromLS && selectedLongitudeFromLS
      ? {
          latitude: selectedLatitudeFromLS,
          longitude: selectedLongitudeFromLS,
        }
      : null,
  zip: selectedZipcodeFromLS,
  sddZip: selectedSddZipcodeFromLS ?? null, //Same day delivery zipcode
  apiError: false,
});

export const setCustomerLatLongLocation = action((latitude: string, longitude: string) => {
  customerLocationState.coordinates = { latitude, longitude };
  const userSelectedCountryLocations = getCountryLocationDataFromLS() ?? {};
  if (!userSelectedCountryLocations[currentCountryCode]) {
    userSelectedCountryLocations[currentCountryCode] = {};
  }
  userSelectedCountryLocations[currentCountryCode].lat = latitude;
  userSelectedCountryLocations[currentCountryCode].long = longitude;
  localStorage.setItem(LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA, JSON.stringify(userSelectedCountryLocations));
});

export const resetCustomerLatLongLocation = action(() => {
  customerLocationState.coordinates = null;
  const userSelectedCountryLocations = getCountryLocationDataFromLS() ?? {};
  if (!userSelectedCountryLocations[currentCountryCode]) return;
  delete userSelectedCountryLocations[currentCountryCode].lat;
  delete userSelectedCountryLocations[currentCountryCode].long;
  localStorage.setItem(LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA, JSON.stringify(userSelectedCountryLocations));
});

export const setCustomerZipcode = action((zipcode: string) => {
  customerLocationState.zip = zipcode;
  const userSelectedCountryLocations = getCountryLocationDataFromLS() ?? {};
  if (!userSelectedCountryLocations[currentCountryCode]) {
    userSelectedCountryLocations[currentCountryCode] = {};
  }
  userSelectedCountryLocations[currentCountryCode].zip = zipcode;
  localStorage.setItem(LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA, JSON.stringify(userSelectedCountryLocations));
});

export const resetCustomerZipcode = action(() => {
  customerLocationState.zip = null;
  const userSelectedCountryLocations = getCountryLocationDataFromLS() ?? {};
  if (!userSelectedCountryLocations[currentCountryCode]) return;
  delete userSelectedCountryLocations[currentCountryCode].zip;
  localStorage.setItem(LOCAL_STORAGE_KEY_COUNTRY_LOCATION_DATA, JSON.stringify(userSelectedCountryLocations));
});

export const setCustomerSddZipcode = action((zipcode: string) => {
  customerLocationState.sddZip = zipcode;
  localStorage.setItem(LOCAL_STORAGE_KEY_SDD_ZIPCODE, zipcode);
});

export const setCustomerLocationErrorState = action((isError: boolean) => {
  customerLocationState.apiError = isError;
});
